// The telemetry sink (decision 0064): off until the player opts in; the game's event set (data) turns its events into
// telemetry events; error reports carry the device, the build and the last few events; field samples carry frame-time
// percentiles, load times and the tier. Records go to every configured backend that accepts their kind, each through
// its own batcher (size and age limits, retry with backoff), so a slow backend never holds up another.
//
// Consent is the gate: before the player opts in (and after they opt out) nothing is recorded, queued or sent; the
// recent-events ring, which error reports carry, also fills only while opted in. An opt-out drops whatever is queued.
// `TELEMETRY_FAULTS.sendBeforeOptIn` removes the gate (the planted defect a game's sink check must catch).
import { createBatcher, realTimers, type Batcher, type BatchLimits, type BatcherStats, type Timers } from "./batcher.js";
import type { Json, RecordKind, TelemetryBackend, TelemetryRecord } from "./backends.js";
import { compileEventSet, type Derive, type EventRule, type Primitive } from "./events.js";

/** Planted defects (engine/CLAUDE.md): read on every record. */
export const TELEMETRY_FAULTS = {
  /** Skip the consent gate: records are made and sent before the player opts in. */
  sendBeforeOptIn: false,
};

/** What every record's context says about where it came from. */
export interface TelemetryContext {
  /** The game's build ("0.1.0+dev"). */
  build: string;
  /** Coarse device facts (kind, OS family, GPU…): no identifiers. */
  device: { [k: string]: Json };
  /** The quality tier in force. */
  tier?: string;
}

/** An error to report. */
export interface ErrorInput {
  /** Where it came from: "uncaught", "rejection", "system" (a fail-soft shutdown), "boot", "manual"… */
  source: string;
  message: string;
  stack?: string;
  /** More structured detail (a system's name and module, a boot step). */
  detail?: { [k: string]: Json };
}

/** A field performance sample (frame times from the game's sampler, e.g. `@rpg/perf`'s `createFieldSampler`). */
export interface FieldSampleInput {
  /** Frame-time percentiles over the window, ms. */
  frameMs: { p50: number; p95: number; p99: number; worst: number; mean: number };
  /** Frames in the window. */
  frames: number;
  /** Frames over the frame budget. */
  slowFrames?: number;
  /** Load times, ms (boot, first frame, module inits…). */
  loadMs?: { [k: string]: number | null };
  /** Anything else the game measures (draw calls, memory). */
  extra?: { [k: string]: Json };
}

/** Options for {@link createTelemetry}. */
export interface TelemetryOptions {
  backends: readonly TelemetryBackend[];
  /** The event set (the game's table). */
  events: readonly EventRule[];
  /** Fields a game adds per source event before the rules read them. */
  derive?: Readonly<Record<string, Derive>>;
  /** Whether the player has opted in now (the game's setting). Read on every record. */
  optedIn(): boolean;
  /** Build, device and tier, read when a record is made. */
  context(): TelemetryContext;
  batch: BatchLimits;
  /** Events kept for error reports (default 20). */
  recent?: number;
  /** Milliseconds, monotonic (default performance.now). Not the sim's clock: telemetry never touches the sim. */
  now?: () => number;
  /** A new session id (default `randomId`: crypto.randomUUID where the page is a secure context). */
  newId?: () => string;
  timers?: Timers;
}

/** One backend's side, for reports. */
export interface BackendReport {
  name: string;
  configured: boolean;
  accepts: readonly RecordKind[];
  stats: BatcherStats | null;
}

/** What the sink has done. */
export interface TelemetryStats {
  optedIn: boolean;
  session: string | null;
  /** Records made (per kind). */
  made: Record<RecordKind, number>;
  /** Records refused because the player hadn't opted in. */
  refused: number;
  backends: BackendReport[];
}

/** The sink. */
export interface Telemetry {
  /** A source event fired (a kernel event, or one the game raises): the event set decides what, if anything, records. */
  event(source: string, payload?: unknown): void;
  /** Record a named event directly (bypasses the event set; the fields are still cleaned to primitives). */
  track(name: string, data?: Record<string, unknown>): void;
  /** Report an error: device, build and the last few events go with it. */
  error(e: ErrorInput): void;
  /** Record a field performance sample: with the tier and device. */
  sample(s: FieldSampleInput): void;
  /** Tell the sink the consent changed (an opt-in starts a session; an opt-out drops what is queued). */
  consentChanged(): void;
  /** Send everything queued now. */
  flush(): Promise<void>;
  /** The last few events (only while opted in). */
  recent(): TelemetryRecord[];
  stats(): TelemetryStats;
  /** Stop the timers. */
  dispose(): void;
}

/** A random v4 UUID. `crypto.randomUUID` exists only in secure contexts (https or localhost), so a page opened over
 *  plain http on a LAN (a phone testing a dev server) builds one from `crypto.getRandomValues`, which needs none. */
export function randomId(): string {
  const c = globalThis.crypto;
  if (typeof c?.randomUUID === "function") return c.randomUUID();
  const b = new Uint8Array(16);
  c.getRandomValues(b);
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

const KINDS: RecordKind[] = ["event", "error", "sample"];

/** Make the sink. The source events the event set names are listed by `sources()` on its result, for the game to
 *  subscribe to. */
export function createTelemetry(o: TelemetryOptions): Telemetry & { sources(): string[] } {
  const now = o.now ?? (() => performance.now());
  const newId = o.newId ?? randomId;
  const set = compileEventSet(o.events, o.derive);
  const keep = Math.max(0, o.recent ?? 20);
  const live = o.backends.filter((b) => b.configured);
  const batchers = new Map<TelemetryBackend, Batcher<TelemetryRecord>>(
    live.map((b) => [b, createBatcher<TelemetryRecord>({ ...o.batch, timers: o.timers ?? realTimers, send: (batch) => b.send(batch) })]),
  );
  const made: Record<RecordKind, number> = { event: 0, error: 0, sample: 0 };
  let refused = 0;
  let session: string | null = null;
  let started = 0;
  let seq = 0;
  let recent: TelemetryRecord[] = [];

  const allowed = (): boolean => TELEMETRY_FAULTS.sendBeforeOptIn || o.optedIn();
  /** Start a session (the first record after an opt-in), with its session-start event. */
  const ensureSession = (): string => {
    if (session) return session;
    session = newId();
    started = now();
    seq = 0;
    set.reset();
    const c = o.context();
    push("event", "session-start", { build: c.build, tier: c.tier ?? null, device: (c.device.kind as Json) ?? null });
    return session;
  };
  const push = (kind: RecordKind, name: string, data: TelemetryRecord["data"]): void => {
    const s = ensureSession();
    const r: TelemetryRecord = { kind, name, session: s, seq: ++seq, t: Math.round(now() - started), build: o.context().build, data };
    made[kind]++;
    if (kind === "event" && keep > 0) {
      recent.push(r);
      if (recent.length > keep) recent = recent.slice(-keep);
    }
    for (const [b, q] of batchers) if (b.accepts.includes(kind)) q.add(r);
  };
  const gate = (): boolean => {
    if (allowed()) return true;
    refused++;
    return false;
  };
  const cleanData = (d: Record<string, unknown> = {}): Record<string, Primitive> => {
    const out: Record<string, Primitive> = {};
    for (const [k, v] of Object.entries(d)) {
      if (v === null || typeof v === "boolean" || (typeof v === "number" && Number.isFinite(v))) out[k] = v as Primitive;
      else if (typeof v === "string") out[k] = v.slice(0, 64);
    }
    return out;
  };

  return {
    sources: () => set.sources(),
    event(source, payload) {
      if (!gate()) return;
      ensureSession();
      for (const m of set.apply(source, payload, (now() - started) / 1000)) push("event", m.name, m.data);
    },
    track(name, data) {
      if (!gate()) return;
      push("event", name, cleanData(data));
    },
    error(e) {
      if (!gate()) return;
      const c = o.context();
      ensureSession();
      push("error", e.source, {
        message: e.message.slice(0, 500),
        stack: (e.stack ?? "").split("\n").slice(0, 12).join("\n"),
        detail: e.detail ?? {},
        device: c.device,
        tier: c.tier ?? null,
        build: c.build,
        recent: recent.map((r) => ({ name: r.name, t: r.t, data: r.data })),
      });
    },
    sample(s) {
      if (!gate()) return;
      const c = o.context();
      push("sample", "field", { frameMs: { ...s.frameMs }, frames: s.frames, slowFrames: s.slowFrames ?? null, loadMs: s.loadMs ?? {}, tier: c.tier ?? null, device: c.device, extra: s.extra ?? {} });
    },
    consentChanged() {
      if (allowed()) return void ensureSession();
      // an opt-out: nothing more leaves, and what was queued is dropped; the next opt-in is a new session
      for (const q of batchers.values()) q.clear();
      session = null;
      recent = [];
    },
    flush: () => Promise.all([...batchers.values()].map((q) => q.flush())).then(() => undefined),
    recent: () => recent.map((r) => structuredClone(r)),
    stats: () => ({
      optedIn: o.optedIn(),
      session,
      made: { ...made },
      refused,
      backends: o.backends.map((b) => ({ name: b.name, configured: b.configured, accepts: b.accepts, stats: batchers.get(b)?.stats() ?? null })),
    }),
    dispose() {
      for (const q of batchers.values()) q.dispose();
    },
  };
}

export { KINDS as RECORD_KINDS };
